import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { SpecialModal } from './SpecialModal';

const dropdownOptions = [
  ['3개월 이내', '1년 이내', '3년 이내', '5년 이내', '10년 이내'],
  ['주변 500m', '주변 1km', '주변 3km'],
  ['5개', '10개', '20개'],
];

const dropdownTitles = ['기간', '범위', '검색 갯수'];

interface StatsFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  auction?: boolean;
  onChangeOption: (val: {
    selectedOptions: string[];
    specialValue: string;
  }) => void;
}

export const StatsFilterModal = ({
  isOpen,
  onClose,
  auction = false,
  onChangeOption,
}: StatsFilterModalProps) => {
  const defaultValues = [['3년 이내'], ['주변 3km'], ['5개']];

  // 실제 선택값
  const [selectedOptions, setSelectedOptions] =
    useState<string[][]>(defaultValues);

  // 모달 내 임시 선택값
  const [tempSelectedOptions, setTempSelectedOptions] =
    useState<string[][]>(defaultValues);

  const [isSpecialModalOpen, setIsSpecialModalOpen] = useState(false);
  const [specialValue, setSpecialValue] = useState<string>('');
  const [special, setSpecial] = useState<[string, string] | []>([]);
  const [specialChoice, setSpecialChoice] = useState<string>('none');
  const [specialRadioChoice, setSpecialRadioChoice] = useState<'all' | 'any'>(
    'all',
  );
  const [openDropdowns, setOpenDropdowns] = useState<boolean[]>(
    dropdownOptions.map(() => false),
  );

  const toggleOption = (dropdownIndex: number, option: string) => {
    setSelectedOptions((prev) => {
      const newSelected = prev.map((arr, i) =>
        i === dropdownIndex ? [option] : arr,
      );
      return newSelected;
    });
  };

  const toggleDropdown = (idx: number) => {
    setOpenDropdowns((prev) => {
      const newState = [...prev];
      newState[idx] = !newState[idx];
      return newState;
    });
  };

  const handleApply = () => {
    const flattened = tempSelectedOptions.flat();

    const selectedItems =
      specialChoice && specialChoice.trim().length > 0
        ? specialChoice.split(',').filter((s) => s && s.trim().length > 0)
        : [];
    const specialValueStr =
      selectedItems.length > 0
        ? [specialRadioChoice, ...selectedItems].join(',')
        : '';

    const resultArray = [...flattened];

    onChangeOption({
      selectedOptions: resultArray,
      specialValue: specialValueStr,
    });

    setSelectedOptions(tempSelectedOptions);
    onClose();
  };

  const handleReset = () => {
    setTempSelectedOptions(defaultValues);
    setOpenDropdowns(dropdownOptions.map(() => false));
    setSpecialValue('');
    setSpecialRadioChoice('all');
    setSpecialChoice('');
  };

  const handleClose = () => {
    setOpenDropdowns(dropdownOptions.map(() => false));
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setTempSelectedOptions(selectedOptions);
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <div>
          <Header>
            <CloseButton onClick={handleClose}>
              <img src="/assets/images/arrow/arrow_down.svg" alt="닫기" />
            </CloseButton>
            <h3>{auction ? '낙찰 사례 검색' : '거래 사례 검색'}</h3>
          </Header>

          <DropdownContainer>
            {dropdownOptions.map((options, idx) => (
              <div key={idx}>
                <Dropdown>
                  <DropdownTitle
                    as="button"
                    type="button"
                    onClick={() => toggleDropdown(idx)}
                  >
                    <LeftContent>{dropdownTitles[idx]}</LeftContent>

                    <ArrowImage
                      isOpen={openDropdowns[idx]}
                      src="assets/images/arrow/arrow_down.svg"
                      alt="arrow"
                    />
                  </DropdownTitle>
                </Dropdown>

                {openDropdowns[idx] && (
                  <OptionsWrapper>
                    <Options>
                      {options.map((option) => {
                        const isSelected =
                          tempSelectedOptions[idx].includes(option);
                        return (
                          <OptionItem
                            key={option}
                            onClick={(e) => {
                              e.stopPropagation();
                              setTempSelectedOptions((prev) => {
                                const newSelected = prev.map((arr, i) =>
                                  i === idx ? [option] : arr,
                                );
                                return newSelected;
                              });
                            }}
                            $selected={isSelected}
                          >
                            <span>{option}</span>
                            {isSelected && (
                              <img
                                src="assets/images/check/blue_check_24.svg"
                                alt="선택됨"
                              />
                            )}
                          </OptionItem>
                        );
                      })}
                    </Options>
                  </OptionsWrapper>
                )}
              </div>
            ))}
            {auction && (
              <Dropdown>
                <DropdownTitle
                  as="button"
                  type="button"
                  onClick={() => setIsSpecialModalOpen(true)}
                >
                  <LeftContent>특수조건</LeftContent>
                  <img
                    style={{
                      width: '20px',
                      height: '20px',
                      marginLeft: '8px',
                      transform: 'rotate(-90deg)',
                    }}
                    src="assets/images/arrow/arrow_down.svg"
                    alt="arrow"
                  />
                </DropdownTitle>
              </Dropdown>
            )}
          </DropdownContainer>
        </div>
        <div>
          <ButtonContainer>
            <CancleBtn type="button" onClick={handleReset}>
              <img src="/assets/images/icon/reset.svg" alt="초기화" />
              초기화
            </CancleBtn>

            <ApplyBtn type="button" onClick={handleApply}>
              사례 검색
            </ApplyBtn>
          </ButtonContainer>
        </div>
      </ModalContent>
      {isSpecialModalOpen && (
        <SpecialModal
          isOpen={isSpecialModalOpen}
          onClose={() => setIsSpecialModalOpen(false)}
          value={specialChoice}
          initialRadio={specialRadioChoice}
          onChangeOption={(val) => {
            setSpecialRadioChoice(val[0]);
            setSpecialChoice(val[1]);
          }}
        />
      )}
    </ModalOverlay>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  max-width: 900px;
  min-height: 100vh;
  overflow-x: hidden;
  margin: 0 auto;
  inset: 0;
  background: #fff;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 16px;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: #fff;
  padding: 6px;
  border-radius: 8px;
  height: 100%;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 20px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;

  img {
    width: 24px;
    rotate: 90deg;
  }
`;

const Description = styled.div`
  p {
    margin: 4px 0;
  }

  p:first-child {
    font-weight: 500;
    font-size: 18px;
    line-height: 28px;
    letter-spacing: -0.02em;
    color: #424854;
  }

  p:nth-child(2) {
    font-weight: 500;
    font-size: 15px;
    line-height: 22px;
    letter-spacing: -0.02em;
    color: #787d87;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 30px;
  margin-top: 8px;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;

  label {
    display: flex;
    align-items: center;
    gap: 5px;
    color: #787d87;
    cursor: pointer;

    input {
      margin-right: 5px;
    }

    input:checked + span {
      color: #0c0e12;
    }
  }
`;

const DropdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 16px;
  width: 100%;
`;

const Dropdown = styled.div`
  display: flex;
  background: #f4f5f7;
  border: none;
  outline: none;
  box-shadow: none;
  border-radius: 8px;
  padding: 8px 12px;
  width: 100%;
  height: 48px;
  margin-bottom: 8px;
  cursor: pointer;
`;

const DropdownTitle = styled.div`
  all: unset;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  font-weight: 500;
  font-size: 15px;
  line-height: 22px;
  cursor: pointer;
`;

const LeftContent = styled.div`
  gap: 4px;

  span {
    position: absolute;
    right: 40px;
    font-weight: 700;
    font-style: Bold;
    font-size: 15px;
    color: #3f6ef1;
  }
`;

const ArrowImage = styled.img<{ isOpen: boolean }>`
  width: 20px;
  height: 20px;
  margin-left: 8px;
  transition: transform 0.2s ease;
  transform: rotate(${(props) => (props.isOpen ? '180deg' : '')});
`;

const CheckboxLabel = styled.label`
  display: block;
  margin-bottom: 10px;

  input {
    margin-right: 6px;
  }

  span {
    color: #787d87;
  }

  input:checked + span {
    color: #0c0e12;
  }
`;

const OptionsWrapper = styled.div`
  position: relative;
  z-index: 2;
`;

const Options = styled.div`
  font-weight: 500;
  font-size: 13px;
  padding: 4px 0;
  line-height: 20px;
  letter-spacing: -2%;
  color: #787d87;
  display: block;
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 10px;
  margin: 10px 0;
`;

const CancleBtn = styled.button`
  display: flex;
  height: 44px;
  gap: 2px;
  angle: 0 deg;
  opacity: 1;
  border-radius: 8px;
  padding-right: 20px;
  padding-left: 18px;
  background: #f4f5f7;
  font-weight: 500;
  font-size: 15px;
  line-height: 22px;
  cursor: pointer;
  border: none;
  outline: none;
  align-items: center;

  img {
    width: 20px;
  }
`;

const ApplyBtn = styled.button<{ disabled?: boolean }>`
  flex: 2;
  height: 44px;
  gap: 10px;
  border-radius: 8px;
  text-align: center;
  background: #000000;
  color: ${({ disabled }) => (disabled ? '#F4F5F74A' : '#fff')};
  font-weight: 500;
  font-size: 15px;
  line-height: 22px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  border: none;
  outline: none;
`;

const OptionItem = styled.div<{ $selected?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 8px;
  font-weight: 500;
  font-size: 14px;
  color: ${({ $selected }) => ($selected ? '#0c0e12' : '#787d87')};
  cursor: pointer;
`;
